const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/
const TIME_RE = /^\d{2}:\d{2}$/

export type TemporaryHoursDay = {
  date: string
  dayLabel: string
  opens: string | null
  closes: string | null
}

export type TemporaryHoursData = {
  startDate: string
  endDate: string
  days: TemporaryHoursDay[]
}

function parseDateParts(value: string) {
  if (!ISO_DATE_RE.test(value)) return null

  const [year, month, day] = value.split("-").map(Number)
  const parsed = new Date(year, month - 1, day)

  if (
    parsed.getFullYear() !== year ||
    parsed.getMonth() !== month - 1 ||
    parsed.getDate() !== day
  ) {
    return null
  }

  return { year, month, day }
}

function isValidTimeValue(value: unknown): value is string | null {
  return value === null || (typeof value === "string" && TIME_RE.test(value))
}

function isTemporaryHoursDay(value: unknown): value is TemporaryHoursDay {
  if (!value || typeof value !== "object") return false

  const day = value as Record<string, unknown>

  return (
    typeof day.dayLabel === "string" &&
    parseDateParts(typeof day.date === "string" ? day.date : "") !== null &&
    isValidTimeValue(day.opens) &&
    isValidTimeValue(day.closes)
  )
}

export function getLocalDateKey(date = new Date()) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, "0")
  const day = String(date.getDate()).padStart(2, "0")

  return `${year}-${month}-${day}`
}

export function formatDateShort(dateKey: string) {
  const parts = parseDateParts(dateKey)
  if (!parts) return dateKey

  return `${String(parts.day).padStart(2, "0")}.${String(parts.month).padStart(2, "0")}`
}

export function parseTemporaryHours(raw: string | null | undefined) {
  if (!raw) return null

  try {
    const data = JSON.parse(raw) as Record<string, unknown>

    if (
      parseDateParts(
        typeof data.startDate === "string" ? data.startDate : ""
      ) === null ||
      parseDateParts(typeof data.endDate === "string" ? data.endDate : "") ===
        null ||
      !Array.isArray(data.days) ||
      data.days.length === 0 ||
      !data.days.every(isTemporaryHoursDay)
    ) {
      return null
    }

    return data as TemporaryHoursData
  } catch {
    return null
  }
}

export function isTemporaryHoursActive(
  data: TemporaryHoursData,
  today = getLocalDateKey()
) {
  return today >= data.startDate && today <= data.endDate
}
